/**
 * Milkath localization helpers — India (en-IN, INR, Asia/Kolkata, DD MMM YYYY).
 *
 * Every page uses these helpers instead of ad-hoc Intl.NumberFormat calls,
 * so switching locales in the future is a one-file change.
 */

const inrFull = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const inrDecimal = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberIN = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** Full Indian rupee formatting — ₹1,25,000. */
export const formatINR = (n) => inrFull.format(Number(n || 0));

/** ₹1,25,000.75 — used for line-level totals when paise matter. */
export const formatINRDecimal = (n) => inrDecimal.format(Number(n || 0));

/**
 * Short Indian rupee — ₹850, ₹75K, ₹3.4L, ₹2.1Cr.
 * K = 1,000 (thousand). L = 1,00,000 (lakh). Cr = 1,00,00,000 (crore).
 */
export function formatINRShort(n) {
  const x = Number(n || 0);
  const abs = Math.abs(x);
  const sign = x < 0 ? "-" : "";
  if (abs >= 1_00_00_000) return `${sign}₹${trim(abs / 1_00_00_000)}Cr`;
  if (abs >= 1_00_000)    return `${sign}₹${trim(abs / 1_00_000)}L`;
  if (abs >= 1_000)       return `${sign}₹${trim(abs / 1_000)}K`;
  return `${sign}₹${Math.round(abs)}`;
}
function trim(n) {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** Plain Indian number — 12,45,630. */
export const formatNumber = (n) => numberIN.format(Number(n || 0));

/** ISO date -> "01 Jul 2026" in Asia/Kolkata timezone. Returns "—" for empty. */
export function formatDate(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  });
}

/** ISO date -> "01 Jul 2026, 3:45 pm" in Asia/Kolkata timezone. */
export function formatDateTime(value) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

/** Percentage — accepts a fraction (0.045) OR a whole number (4.5) and normalizes. */
export function formatPct(n, { asFraction = null, digits = 1 } = {}) {
  if (n == null || Number.isNaN(n)) return "—";
  const isFraction = asFraction ?? Math.abs(n) <= 1;
  const val = isFraction ? n * 100 : n;
  const sign = val >= 0 ? "+" : "";
  return `${sign}${val.toFixed(digits)}%`;
}

export const LOCALE_DEFAULTS = {
  locale: "en-IN",
  currency: "INR",
  currencySymbol: "₹",
  timezone: "Asia/Kolkata",
  dateFormat: "DD MMM YYYY",
};
